import React from 'react';
import { Row, Col, Form, Input, DatePicker, Upload, Button, Typography } from 'antd';
import { Upload as UploadIcon, X as CloseIcon } from 'lucide-react';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Text } = Typography;

interface UpdateIdDocumentsSectionProps {
  citizenIdFrontPreview?: string | null;
  citizenIdBackPreview?: string | null;
  passportPreview?: string | null;
  passportAdditionalPreview?: string | null;
  isUploading?: boolean;
  onBeforeUploadCitizenFront?: (file: File) => boolean | Promise<boolean>;
  onBeforeUploadCitizenBack?: (file: File) => boolean | Promise<boolean>;
  onBeforeUploadPassportCover?: (file: File) => boolean | Promise<boolean>;
  onBeforeUploadPassportPhoto?: (file: File) => boolean | Promise<boolean>;
  onClearCitizenFront?: () => void;
  onClearCitizenBack?: () => void;
  onClearPassportCover?: () => void;
  onClearPassportPhoto?: () => void;
  userId?: number;
}

export const UpdateIdDocumentsSection: React.FC<UpdateIdDocumentsSectionProps> = ({
  citizenIdFrontPreview,
  citizenIdBackPreview,
  passportPreview,
  passportAdditionalPreview,
  isUploading = false,
  onBeforeUploadCitizenFront,
  onBeforeUploadCitizenBack,
  onBeforeUploadPassportCover,
  onBeforeUploadPassportPhoto,
  onClearCitizenFront,
  onClearCitizenBack,
  onClearPassportCover,
  onClearPassportPhoto,
}) => {

  return (
    <div className={styles.idDocumentsSection}>
      <div className={styles.idDocumentsGroup}>

        <Row gutter={24} className={styles.formRow}>
          <Col span={12}>
            <Form.Item name="idNumber" label="CCCD/CMND">
              <Input
                placeholder="Nhập số CCCD/CMND"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="idIssuePlace" label="Nơi cấp">
              <Input placeholder="Nhập nơi cấp" size="large" className={styles.input} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles.formRow}>
          <Col span={12}>
            <Form.Item name="idIssueDate" label="Ngày cấp">
              <DatePicker
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="idExpiryDate" label="Ngày hết hạn">
              <DatePicker
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles.idDocumentsUploadRow}>
          <Col span={12}>
            <Text className={styles.idDocumentsUploadLabel}>Ảnh CCCD mặt trước</Text>
            <Upload.Dragger
              name="citizenIdFrontImage"
              multiple={false}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
              className={styles.idDocumentsDragger}
              beforeUpload={onBeforeUploadCitizenFront}
            >
              {citizenIdFrontPreview ? (
                <div className={styles.idDocumentsPreviewWrapper}>
                  <div className={styles.idDocumentsPreviewInner}>
                    <button
                      type="button"
                      className={styles.idDocumentsRemoveButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearCitizenFront?.();
                      }}
                    >
                      <CloseIcon size={16} />
                    </button>
                    <img
                      src={citizenIdFrontPreview}
                      alt="Ảnh CCCD mặt trước"
                      className={styles.idDocumentsPreviewImage}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.idDocumentsDraggerContent}>
                  <UploadIcon size={32} className={styles.idDocumentsUploadIcon} />
                  <p className={styles.idDocumentsDraggerTitle}>
                    Kéo thả ảnh vào đây để tải lên hoặc
                  </p>
                  <Button type="primary" size="middle">
                    Chọn ảnh
                  </Button>
                  <p className={styles.idDocumentsDraggerHint}>
                    Hỗ trợ các định dạng JPG, PNG, WebP. Dung lượng tối đa 5MB.
                  </p>
                </div>
              )}
            </Upload.Dragger>
          </Col>
          <Col span={12}>
            <Text className={styles.idDocumentsUploadLabel}>Ảnh CCCD mặt sau</Text>
            <Upload.Dragger
              name="citizenIdBackImage"
              multiple={false}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
              className={styles.idDocumentsDragger}
              beforeUpload={onBeforeUploadCitizenBack}
            >
              {citizenIdBackPreview ? (
                <div className={styles.idDocumentsPreviewWrapper}>
                  <div className={styles.idDocumentsPreviewInner}>
                    <button
                      type="button"
                      className={styles.idDocumentsRemoveButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearCitizenBack?.();
                      }}
                    >
                      <CloseIcon size={16} />
                    </button>
                    <img
                      src={citizenIdBackPreview}
                      alt="Ảnh CCCD mặt sau"
                      className={styles.idDocumentsPreviewImage}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.idDocumentsDraggerContent}>
                  <UploadIcon size={32} className={styles.idDocumentsUploadIcon} />
                  <p className={styles.idDocumentsDraggerTitle}>
                    Kéo thả ảnh vào đây để tải lên hoặc
                  </p>
                  <Button type="primary" size="middle">
                    Chọn ảnh
                  </Button>
                  <p className={styles.idDocumentsDraggerHint}>
                    Hỗ trợ các định dạng JPG, PNG, WebP. Dung lượng tối đa 5MB.
                  </p>
                </div>
              )}
            </Upload.Dragger>
          </Col>
        </Row>
      </div>

      <div className={styles.idDocumentsGroup}>
        <Row gutter={24} className={styles.formRow}>
          <Col span={8}>
            <Form.Item name="passportType" label="Loại hộ chiếu">
              <Input placeholder="Loại hộ chiếu" size="large" className={styles.input} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="passportNumber" label="Số hộ chiếu">
              <Input placeholder="Số hộ chiếu" size="large" className={styles.input} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="passportIssuePlace" label="Nơi cấp">
              <Input placeholder="Nơi cấp" size="large" className={styles.input} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles.formRow}>
          <Col span={12}>
            <Form.Item name="passportIssueDate" label="Ngày cấp">
              <DatePicker
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="passportExpiryDate" label="Ngày hết hạn">
              <DatePicker
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                size="large"
                className={styles.input}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles.idDocumentsUploadRow}>
          <Col span={12}>
            <Text className={styles.idDocumentsUploadLabel}>Ảnh hộ chiếu</Text>
            <Upload.Dragger
              name="passportImage"
              multiple={false}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
              className={styles.idDocumentsDragger}
              beforeUpload={onBeforeUploadPassportCover}
            >
              {passportPreview ? (
                <div className={styles.idDocumentsPreviewWrapper}>
                  <div className={styles.idDocumentsPreviewInner}>
                    <button
                      type="button"
                      className={styles.idDocumentsRemoveButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearPassportCover?.();
                      }}
                    >
                      <CloseIcon size={16} />
                    </button>
                    <img
                      src={passportPreview}
                      alt="Ảnh hộ chiếu"
                      className={styles.idDocumentsPreviewImage}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.idDocumentsDraggerContent}>
                  <UploadIcon size={32} className={styles.idDocumentsUploadIcon} />
                  <p className={styles.idDocumentsDraggerTitle}>
                    Kéo thả ảnh vào đây để tải lên hoặc
                  </p>
                  <Button type="primary" size="middle">
                    Chọn ảnh
                  </Button>
                  <p className={styles.idDocumentsDraggerHint}>
                    Hỗ trợ các định dạng JPG, PNG, WebP. Dung lượng tối đa 5MB.
                  </p>
                </div>
              )}
            </Upload.Dragger>
          </Col>
          <Col span={12}>
            <Text className={styles.idDocumentsUploadLabel}>Ảnh khác (nếu có)</Text>
            <Upload.Dragger
              name="passportAdditionalImage"
              multiple={false}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
              className={styles.idDocumentsDragger}
              beforeUpload={onBeforeUploadPassportPhoto}
            >
              {passportAdditionalPreview ? (
                <div className={styles.idDocumentsPreviewWrapper}>
                  <div className={styles.idDocumentsPreviewInner}>
                    <button
                      type="button"
                      className={styles.idDocumentsRemoveButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearPassportPhoto?.();
                      }}
                    >
                      <CloseIcon size={16} />
                    </button>
                    <img
                      src={passportAdditionalPreview}
                      alt="Ảnh khác hộ chiếu"
                      className={styles.idDocumentsPreviewImage}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.idDocumentsDraggerContent}>
                  <UploadIcon size={32} className={styles.idDocumentsUploadIcon} />
                  <p className={styles.idDocumentsDraggerTitle}>
                    Kéo thả ảnh vào đây để tải lên hoặc
                  </p>
                  <Button type="primary" size="middle">
                    Chọn ảnh
                  </Button>
                  <p className={styles.idDocumentsDraggerHint}>
                    Hỗ trợ các định dạng JPG, PNG, WebP. Dung lượng tối đa 5MB.
                  </p>
                </div>
              )}
            </Upload.Dragger>
          </Col>
        </Row>
      </div>
    </div>
  );
};

